import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { store } from './store';
import { Provider } from 'react-redux';

import { TooltipProvider } from '@/components/ui/tooltip';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext.tsx';

// Initialize Sentry - REMOVED PROD CHECK FOR TESTING
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

console.log('🔍 Sentry Configuration Check:');
console.log('Environment:', import.meta.env.MODE);
console.log('Sentry DSN:', sentryDsn ? '✅ Configured' : '❌ Not configured');
console.log('DSN:', sentryDsn);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions for testing
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Environment
    environment: import.meta.env.MODE || 'development',
    release: `swaraah@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    
    // Debug mode - ENABLE THIS
    debug: true, // ✅ This will show Sentry logs in console
    
    // Ignore common errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
    
    beforeSend(event, hint) {
      console.log('📤 Sentry Event:', event);
      console.log('Hint:', hint);
      
      // Filter localhost errors in production
      if (import.meta.env.PROD && event.request?.url?.includes('localhost')) {
        console.log('⏭️ Skipping localhost error');
        return null;
      }
      
      return event;
    },
  });
  
  console.log('✅ Sentry initialized successfully!');
  
  // Test Sentry with a console log
  Sentry.captureMessage('Swaraah AI App Started', 'info');
  
} else {
  console.warn('⚠️ Sentry DSN not configured - error tracking disabled');
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground">We're sorry for the inconvenience.</p>
            {import.meta.env.DEV && (
              <pre className="text-xs bg-muted p-3 rounded text-left overflow-auto max-h-40">
                {error.toString()}
              </pre>
            )}
            <button
              onClick={resetError}
              className="px-4 py-2 bg-theme-primary text-theme-primary-foreground rounded-md hover:bg-theme-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      showDialog={import.meta.env.DEV}
    >
      <ErrorBoundary>
        <Provider store={store}>
          <ThemeProvider defaultTheme="purple-pink">
            <TooltipProvider>
              <App />
            </TooltipProvider>
          </ThemeProvider>
        </Provider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
